import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { X, ShareNetwork, Copy, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ShareDialog({ open, groupCode, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!open) return null;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const link = `${origin}/?code=${encodeURIComponent(groupCode)}`;
  const canShare = typeof navigator !== "undefined" && !!navigator.share;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the link");
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({
        title: "Join my crew",
        text: `Find me at the festival — join group ${groupCode} on unlostcrew:`,
        url: link,
      });
    } catch (e) {}
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 sm:items-center" data-testid="share-dialog">
      <div className="w-full max-w-md border-2 border-black bg-white shadow-[8px_8px_0px_rgba(0,0,0,1)]">
        <div className="flex items-center justify-between border-b-2 border-black bg-accent px-4 py-3 text-white">
          <h2 className="flex items-center gap-2 font-heading text-base font-black uppercase tracking-tight">
            <ShareNetwork weight="bold" size={20} /> Invite your crew
          </h2>
          <button data-testid="share-close" onClick={onClose} className="grid h-9 w-9 place-items-center border-2 border-black bg-white text-black">
            <X weight="bold" size={18} />
          </button>
        </div>

        <div className="flex flex-col items-center p-5">
          <p className="mb-4 text-center font-mono2 text-sm font-medium text-black/60">
            They scan this or tap the link — no typing, straight onto your map.
          </p>

          <div className="border-2 border-black bg-white p-3 shadow-[4px_4px_0px_#EAB308]" data-testid="share-qr">
            <QRCodeSVG value={link} size={190} bgColor="#ffffff" fgColor="#18181b" level="M" />
          </div>

          <div className="mt-4 w-full border-2 border-black bg-background px-3 py-2 text-center">
            <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.2em] text-black/40">Group code</p>
            <p className="font-heading text-2xl font-black uppercase tracking-widest">{groupCode}</p>
          </div>

          <button
            data-testid="share-copy"
            onClick={copy}
            className="mt-4 flex h-12 w-full items-center justify-center gap-2 border-2 border-black bg-white font-mono2 text-xs font-bold uppercase shadow-[3px_3px_0px_rgba(0,0,0,1)] transition-transform duration-150 ease-out active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
          >
            {copied ? <Check weight="bold" size={16} /> : <Copy weight="bold" size={16} />}
            {copied ? "Copied!" : "Copy invite link"}
          </button>

          {canShare && (
            <button
              data-testid="share-native"
              onClick={nativeShare}
              className="mt-2 flex h-14 w-full items-center justify-center gap-2 border-2 border-black bg-primary font-heading text-base font-bold uppercase shadow-[4px_4px_0px_#18181B] transition-transform duration-150 ease-out active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
            >
              <ShareNetwork weight="bold" size={20} /> Share
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
